import DeleteModal from "components/shared/DeleteModal";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IService } from "store/servicesStore";
import { deleteService, getServices } from "store/servicesStore";
import { useAppDispatch, useAppSelector } from "store/store";

import ServicesForm from "./ServicesForm";
import styles from "./style.module.scss";
import { formatRates } from "./utils";

/**
 * Page in Settings to define services and rates applied to it if company uses IRCEC (currently only used by press companies.)
 */
const ServicesPage = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { services, servicesStatus } = useAppSelector((state) => state.services);

    const [modalType, setModalType] = useState("");
    const [currentService, setCurrentService] = useState<IService>();

    const handleOpeningModal = async (type: "create" | "edit" | "delete", service?: IService) => {
        if (service) {
            await setCurrentService(service);
        }
        setModalType(type);
    };

    const actionsOptions: ThreeDotMenuOptions[] = [
        { id: 1, name: t("Services.Table.Update"), action: () => handleOpeningModal("edit", currentService) },
        { id: 2, name: t("Services.Table.Delete"), action: () => handleOpeningModal("delete", currentService) },
    ];

    const formatRatesColumn = (service: IService) => {
        return service.hasIrcec ? (
            <div className={styles["rates-column"]}>
                <span>{t("Services.Table.RACDValue", { value: formatRates(service.racd) })}</span>
                <span>{t("Services.Table.RAAPValue", { value: formatRates(service.raap) })}</span>
                <span>{t("Services.Table.SACDValue", { value: service.sacd })}</span>
            </div>
        ) : (
            <span>-</span>
        );
    };

    const servicesColumns: LastElementTableColumns[] = [
        {
            id: "prestation",
            align: "left",
            label: t("Services.Name"),
            format: (value, id, currentService: IService) => <span>{currentService.name ?? ""}</span>,
        },
        {
            id: "hasIrcec",
            align: "left",
            label: t("Services.HasIrcec"),
            format: (value: number) => (value ? t("Services.Table.Yes") : t("Services.Table.No")),
        },
        {
            id: "options",
            align: "left",
            label: t("Services.Rates"),
            format: (value, id, currentService: IService) => formatRatesColumn(currentService),
        },
        {
            id: "action",
            label: "",
            format: (value, id, currentService: IService) => (
                <ThreeDotMenu options={actionsOptions} onMenuClick={() => setCurrentService(currentService)} />
            ),
        },
    ];

    useEffect(() => {
        if (servicesStatus === "idle") {
            dispatch(getServices());
        }
    }, [servicesStatus]);

    const servicesLoading = ["idle", "pending"].includes(servicesStatus);

    const handleDelete = async () => {
        if (currentService) {
            dispatch(deleteService({ serviceId: currentService?.id }));
        }
    };

    const deleteDescription = (
        <>
            <p>{t("Services.DeleteModal.Description1")}</p>
            <p>{t("Services.DeleteModal.Description2")}</p>
        </>
    );

    return (
        <div className={styles["services-page-container"]}>
            <div className={styles["header"]}>
                <h1>{t("Settings.Service_other")}</h1>
                <StyledButton buttonType="primary" onClick={() => handleOpeningModal("create")}>
                    {t("Services.CreateService")}
                </StyledButton>
            </div>
            <LastElementTable
                data={services}
                columns={servicesColumns}
                max={-1}
                dataType="services"
                isLoading={servicesLoading}
                columnsNumber={4}
            />
            <ModalBase
                open={["create", "edit"].includes(modalType)}
                onClose={() => setModalType("")}
                modalTitle={modalType === "create" ? t("Services.CreateService") : t("Services.UpdateService")}
            >
                <ServicesForm
                    type={modalType}
                    service={currentService}
                    from="services"
                    handleClose={() => {
                        setModalType("");
                        setCurrentService(undefined);
                    }}
                />
            </ModalBase>
            <DeleteModal
                open={modalType === "delete"}
                onClose={() => setModalType("")}
                onDelete={handleDelete}
                elementTitle={t("Services.DeleteModal.ElementName", { name: currentService?.name ?? "" })}
                needsConfirmation
                description={deleteDescription}
                confirmationText={t("Services.DeleteModal.ConfirmationText")}
            />
        </div>
    );
};

export default ServicesPage;
